import React from 'react';
import { User } from '@/domain/entities';

interface UsersTableProps {
  users: User[];
  onViewDetails: (user: User) => void;
  onDelete: (user: User) => void;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onViewDetails,
  onDelete,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  if (!users || users.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow ring-1 ring-black ring-opacity-5 dark:ring-gray-700 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
        Пользователи не найдены
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Пользователь
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Контакты
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Заказы
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Выручка
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Последний заказ
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Создан
              </th>
              <th className="py-2.5 px-3 text-right text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-300 dark:border-gray-600">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <td className="py-2.5 px-4 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center">
                    <img
                      src={user.photoUrl || user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email)}&background=EEF2FF&color=312E81`}
                      alt={user.name || user.email}
                      className="w-10 h-10 rounded-full object-cover bg-gray-100 dark:bg-gray-700 flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email)}&background=EEF2FF&color=312E81`;
                      }}
                    />
                    <div className="ml-3 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.name || '—'}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700">
                  <div>{user.username ? `@${user.username}` : '—'}</div>
                  <div>{user.telegramId ? `TG ID: ${user.telegramId}` : ''}</div>
                  <div className="text-xs">{user.hasPassword ? 'Пароль установлен' : 'Только TG'}</div>
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm font-semibold text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                  {user.ordersCount}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm font-semibold text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                  {formatCurrency(user.totalSpent)}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {user.lastOrderAt ? formatDateTime(user.lastOrderAt) : '—'}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {formatDate(user.createdAt)}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onViewDetails(user)}
                      className="px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                    >
                      Подробнее
                    </button>
                    <button
                      onClick={() => onDelete(user)}
                      className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                    >
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

